import { useState, useEffect } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../lib/firebase/config';
import { authService, getUserProfile } from '../lib/firebase/auth';
import { UserDocument } from '@curbside-quest/shared';
import { conditionalLog, conditionalError } from '../lib/utils/logger';

export interface AuthState {
  user: User | null;
  userProfile: UserDocument | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  role: string | null;
  customClaims: { [key: string]: any } | null;
}

export interface AuthActions {
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  clearError: () => void;
}

export function useAuth(): AuthState & AuthActions {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState<string | null>(null);
  const [customClaims, setCustomClaims] = useState<{ [key: string]: any } | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        try {
          // Fetch user profile from Firestore
          const profile = await getUserProfile(user.uid);
          setUserProfile(profile);
          
          // Check admin status from custom claims
          const idTokenResult = await user.getIdTokenResult();
          conditionalLog('useAuth - Token claims:', idTokenResult.claims);
          conditionalLog('useAuth - Has admin claim:', !!idTokenResult.claims.admin);
          conditionalLog('useAuth - Has moderator claim:', !!idTokenResult.claims.moderator);
          
          const isAdminUser = !!idTokenResult.claims.admin || !!idTokenResult.claims.moderator;
          conditionalLog('useAuth - Setting isAdmin to:', isAdminUser);
          setIsAdmin(isAdminUser);
          setRole(idTokenResult.claims.role as string || 'member');

          // Filter out standard claims to show only custom ones
          const filteredClaims: { [key: string]: any } = {};
          for (const key in idTokenResult.claims) {
            if (!['iss', 'aud', 'auth_time', 'user_id', 'sub', 'iat', 'exp', 'email', 'email_verified', 'firebase', 'picture', 'name'].includes(key)) {
              filteredClaims[key] = idTokenResult.claims[key];
            }
          }
          setCustomClaims(filteredClaims);
        } catch (err) {
          conditionalError('Error fetching user profile:', err);
          setError('Failed to load user profile');
        }
      } else {
        setUserProfile(null);
        setIsAdmin(false);
        setRole(null);
        setCustomClaims(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleAuthAction = async (action: () => Promise<any>) => {
    try {
      setError(null);
      setLoading(true);
      await action();
    } catch (err: any) {
      conditionalError('Auth error:', err);
      setError(getAuthErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const signUpWithEmail = async (email: string, password: string, displayName: string) => {
    await handleAuthAction(() => authService.signUpWithEmail(email, password, displayName));
  };

  const signInWithEmail = async (email: string, password: string) => {
    await handleAuthAction(() => authService.signInWithEmail(email, password));
  };

  const signInWithGoogle = async () => {
    await handleAuthAction(() => authService.signInWithGoogle());
  };

  const signOut = async () => {
    await handleAuthAction(() => authService.signOut());
  };

  const sendPasswordResetEmail = async (email: string) => {
    await handleAuthAction(() => authService.sendPasswordResetEmail(email));
  };

  const clearError = () => {
    setError(null);
  };

  return {
    user,
    userProfile,
    loading,
    error,
    isAdmin,
    role,
    customClaims,
    signUpWithEmail,
    signInWithEmail,
    signInWithGoogle,
    signOut,
    sendPasswordResetEmail,
    clearError,
  };
}

// Helper function to convert Firebase auth error codes to user-friendly messages
function getAuthErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/popup-blocked':
      return 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
    default:
      return 'An error occurred during authentication. Please try again.';
  }
}